(() => {
  const ROOT_SELECTOR = '#infiniteoptions-container';
  const CONTROL_SELECTOR = 'select, input:not([type="hidden"]), textarea';

  const KEYWORDS = {
    wrapping: ['梱包(無料)', '梱包', 'wrapping_free'],
    noshi: ['熨斗', 'のし'],
    noshiName: ['名前入れ(熨斗)', '熨斗名入れ', '名入れ'],
    card: ['メッセージカード'],
    cardName: ['メッセージカード名前入れ', '贈り先の方の御名前', 'カード名前入れ'],
  };

  const NEGATIVE_RE = /(なし|不要|無し|no|none)/i;
  const POSITIVE_RE = /(あり|有り|希望|要|yes)/i;

  const normalize = (value) => (value || '').replace(/\s+/g, '').toLowerCase();

  function getAssociatedLabelText(control, root) {
    if (!control) return '';

    if (control.id) {
      const explicit = root.querySelector(`label[for="${control.id}"]`);
      if (explicit) return explicit.textContent || '';
    }

    const parentLabel = control.closest('label');
    if (parentLabel) return parentLabel.textContent || '';

    let sibling = control.previousElementSibling;
    while (sibling) {
      if (sibling.tagName === 'LABEL') return sibling.textContent || '';
      sibling = sibling.previousElementSibling;
    }

    return '';
  }

  function getControlFingerprint(control, root) {
    const parts = [
      control.name,
      control.id,
      control.placeholder,
      control.getAttribute('aria-label'),
      getAssociatedLabelText(control, root),
      control.closest('[class]')?.className,
    ];
    return normalize(parts.filter(Boolean).join(' '));
  }

  function findControls(root, includeKeywords, excludeKeywords = []) {
    const include = includeKeywords.map(normalize);
    const exclude = excludeKeywords.map(normalize);

    return Array.from(root.querySelectorAll(CONTROL_SELECTOR)).filter((control) => {
      const fp = getControlFingerprint(control, root);
      const includeMatched = include.some((word) => fp.includes(word));
      const excludeMatched = exclude.some((word) => fp.includes(word));
      return includeMatched && !excludeMatched;
    });
  }

  function choosePrimaryControl(candidates) {
    if (!candidates.length) return null;
    return (
      candidates.find((el) => el.tagName === 'SELECT')
      || candidates.find((el) => el.type === 'radio')
      || candidates.find((el) => el.type === 'checkbox')
      || candidates[0]
    );
  }

  function findField(root, includeKeywords, excludeKeywords = []) {
    return choosePrimaryControl(findControls(root, includeKeywords, excludeKeywords));
  }

  function selectedRadioInGroup(root, name) {
    if (!name) return null;
    return root.querySelector(`input[type="radio"][name="${name}"]:checked`);
  }

  function getCurrentValueText(control, root) {
    if (!control) return '';

    if (control.tagName === 'SELECT') {
      const option = control.options[control.selectedIndex];
      return `${control.value} ${option?.textContent || ''}`;
    }

    if (control.type === 'radio') {
      const selected = selectedRadioInGroup(root, control.name);
      const selectedLabel = selected ? getAssociatedLabelText(selected, root) : '';
      return `${selected?.value || ''} ${selectedLabel}`;
    }

    if (control.type === 'checkbox') {
      return control.checked ? 'checked あり yes' : 'unchecked なし no';
    }

    return control.value || '';
  }

  function isAffirmative(control, root) {
    const text = getCurrentValueText(control, root);
    if (!text) return false;
    if (NEGATIVE_RE.test(text)) return false;
    if (POSITIVE_RE.test(text)) return true;

    if (control.tagName === 'SELECT') return control.selectedIndex > 0;
    if (control.type === 'checkbox') return control.checked;
    if (control.type === 'radio') return Boolean(selectedRadioInGroup(root, control.name));

    return Boolean(text.trim());
  }

  function findFieldWrapper(control, root, keywords) {
    if (!control) return null;
    const normalizedKeywords = keywords.map(normalize);

    let current = control.parentElement;
    while (current && current !== root) {
      const text = normalize(current.textContent || '');
      const controls = current.querySelectorAll(CONTROL_SELECTOR).length;
      const keywordMatched = normalizedKeywords.some((word) => text.includes(word));

      if (keywordMatched && controls >= 1 && controls <= 3) {
        return current;
      }
      current = current.parentElement;
    }

    return control.closest('div, p, li, fieldset, tr') || control.parentElement;
  }

  function resetControlValue(control, root) {
    if (!control) return;

    if (control.tagName === 'SELECT') {
      const noneOptionIndex = Array.from(control.options).findIndex((option) => NEGATIVE_RE.test(option.value) || NEGATIVE_RE.test(option.textContent || ''));
      control.selectedIndex = noneOptionIndex >= 0 ? noneOptionIndex : 0;
      control.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (control.type === 'radio') {
      const group = Array.from(root.querySelectorAll(`input[type="radio"][name="${control.name}"]`));
      const noneRadio = group.find((radio) => {
        const label = getAssociatedLabelText(radio, root);
        return NEGATIVE_RE.test(`${radio.value} ${label}`);
      });
      group.forEach((radio) => {
        radio.checked = false;
      });
      if (noneRadio) {
        noneRadio.checked = true;
      }
      const eventTarget = noneRadio || group[0];
      if (eventTarget) eventTarget.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (control.type === 'checkbox') {
      control.checked = false;
      control.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    control.value = '';
    control.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function setWrapperVisibility(wrapper, isVisible, resetTargets = []) {
    if (!wrapper) return;

    wrapper.style.display = isVisible ? '' : 'none';
    wrapper.setAttribute('data-gift-ui-hidden', isVisible ? 'false' : 'true');

    const fields = wrapper.querySelectorAll('input, select, textarea, button');
    fields.forEach((field) => {
      field.disabled = !isVisible;
    });

    if (!isVisible) {
      resetTargets.forEach((field) => {
        resetControlValue(field, wrapper.closest(ROOT_SELECTOR) || document);
      });
    }
  }

  function bindOnce(control, callback) {
    if (!control || control.dataset.giftUiBound === 'true') return;
    control.addEventListener('change', callback);
    control.addEventListener('input', callback);
    control.dataset.giftUiBound = 'true';
  }

  function applyForContainer(root) {
    const wrappingControl = findField(root, KEYWORDS.wrapping);
    const noshiControl = findField(root, KEYWORDS.noshi, ['名前入れ', 'メッセージカード']);
    const noshiNameControl = findField(root, KEYWORDS.noshiName, ['メッセージカード']);
    const cardControl = findField(root, KEYWORDS.card, ['名前入れ']);
    const cardNameControl = findField(root, KEYWORDS.cardName);

    if (!wrappingControl && !noshiControl && !cardControl) return;

    const wrappingValue = getCurrentValueText(wrappingControl, root);
    const ribbonOn = /リボン/.test(wrappingValue);
    const noshiOn = isAffirmative(noshiControl, root);
    const cardOn = isAffirmative(cardControl, root);
    const cardVisible = noshiOn || ribbonOn;

    const noshiNameWrapper = findFieldWrapper(noshiNameControl, root, KEYWORDS.noshiName);
    const cardWrapper = findFieldWrapper(cardControl, root, KEYWORDS.card);
    const cardNameWrapper = findFieldWrapper(cardNameControl, root, KEYWORDS.cardName);

    setWrapperVisibility(noshiNameWrapper, noshiOn, noshiOn ? [] : [noshiNameControl]);
    setWrapperVisibility(cardWrapper, cardVisible, cardVisible ? [] : [cardControl]);
    setWrapperVisibility(cardNameWrapper, noshiOn && cardOn, noshiOn && cardOn ? [] : [cardNameControl]);

    const refresh = () => applyForContainer(root);
    bindOnce(wrappingControl, refresh);
    bindOnce(noshiControl, refresh);
    bindOnce(cardControl, refresh);
  }

  function observeContainer(root) {
    if (!root || root.dataset.giftUiObserved === 'true') return;

    let rafId = null;
    const observer = new MutationObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        applyForContainer(root);
      });
    });

    observer.observe(root, { childList: true, subtree: true });
    root.dataset.giftUiObserved = 'true';
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      applyForContainer(root);
      observeContainer(root);
    });
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();